// src/pages/Deck/MyDecks.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserDecks, deleteDeck } from '../../api/deckService';
import './MyDecks.css';

const MyDecks = ({ embedded = false }) => {
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Charger les decks de l'utilisateur
  useEffect(() => {
    const fetchDecks = async () => {
      try {
        setLoading(true);
        const response = await getUserDecks();
        setDecks(response.decks);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement des decks. Veuillez réessayer.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDecks();
  }, []);

  // Supprimer un deck
  const handleDeleteDeck = async (deckId, e) => {
    e.stopPropagation();
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce deck ?')) {
      try {
        await deleteDeck(deckId);
        setDecks(decks.filter(deck => deck.id !== deckId));
      } catch (err) {
        setError(`Erreur lors de la suppression du deck: ${err.message}`);
      }
    }
  };

  // Créer un nouveau deck
  const handleCreateDeck = () => {
    navigate('/decks/builder');
  };

  // Aller à l'éditeur de deck
  const handleEditDeck = (deckId) => {
    navigate(`/decks/builder/${deckId}`);
  };

  // Générer une couleur de fond basée sur l'élément
  const getElementColor = (element) => {
    const elementColors = {
      'Feu': 'var(--color-fire)',
      'Eau': 'var(--color-water)',
      'Terre': 'var(--color-earth)',
      'Vent': 'var(--color-air)',
      'Lumière': 'var(--color-light)',
      'Ténèbres': 'var(--color-dark)',
      'Arc-en-Ciel': 'linear-gradient(to right, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #8b00ff)'
    };
    return elementColors[element] || 'var(--color-primary)';
  };

  if (loading) {
    return (
      <div className="decks-loading">
        <div className="loading-spinner"></div>
        <p>Chargement de vos decks...</p>
      </div>
    );
  }

  return (
    <div className={embedded ? 'my-decks-container my-decks-embedded' : 'my-decks-container'}>
      <div className="decks-header">
        {embedded ? null : <h1>Mes Decks</h1>}
        <button onClick={handleCreateDeck} className="create-deck-button">
          Créer un nouveau deck
        </button>
      </div>
      
      {error && <div className="decks-error">{error}</div>}
      
      {decks.length === 0 ? (
        <div className="no-decks-message">
          <p>Vous n'avez pas encore de decks.</p>
          <p>Créez votre premier deck pour commencer à jouer !</p>
          <button onClick={handleCreateDeck} className="create-deck-button-large">
            Créer mon premier deck
          </button>
        </div>
      ) : (
        <div className="decks-grid">
          {decks.map((deck) => (
            <div 
              key={deck.id}
              className="deck-card"
              onClick={() => handleEditDeck(deck.id)}
              style={{ 
                borderColor: getElementColor(deck.element),
                backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url('/card-backs/${deck.element.toLowerCase()}.png')`
              }}
            >
              <div className="deck-header">
                <h2>{deck.name}{deck.mode === 'ranked' && (
                <span className="deck-mode-badge ranked" title="Deck classé : cartes en escrow/verrouillées interdites">🏆 Classé</span>
              )}</h2>
                <span className="deck-element" style={{ backgroundColor: getElementColor(deck.element) }}>
                  {deck.element}
                </span>
              </div>
              
              <div className="deck-info">
                <p className="deck-description">{deck.description}</p>
                <div className="deck-meta">
                  <span>{deck.cards.length} cartes</span>
                  {deck.difficulty && (
                    <span className={`deck-difficulty difficulty-${deck.difficulty.toLowerCase()}`}>
                      {deck.difficulty}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="deck-actions">
                <button 
                  className="edit-deck-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditDeck(deck.id);
                  }}
                >
                  Modifier
                </button>
                <button 
                  className="delete-deck-button"
                  onClick={(e) => handleDeleteDeck(deck.id, e)}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyDecks;